import logger from '../log';
import ContentItem from '../api/content/ContentItem';
import BaseInteraction from './BaseInteraction';
import { SourceType } from '../common/constants';
import { checkType, TypeDeterminer } from '../util/checkTypes';
import { FactoryOptionsBase } from '@kollective-frontend/ksdk-api';
import { TechApi } from '../bootloader/tech';

const log = logger.getLogger(__filename);

export default abstract class FactoryPlayerInteractionBase<
  T extends FactoryOptionsBase
> extends BaseInteraction<T> {
  constructor(options: T, tech: TechApi) {
    super(options, tech);
  }

  abstract setSourceType(): TypeDeterminer;

  setSource(url: string, mimeType?: string, hasRetried = false): Promise<void> {
    const player = this.player;
    const result = checkType(player, this.setSourceType());
    if (!result.isType || !player.setSource || !player.getCurrentSource) {
      const data = result.data || ({} as Record<string, string>);
      log.debug(
        'Unable to setSource setup options did not include that functionality',
        data.messages
      );
      return Promise.resolve();
    }
    log.debug(`setSource: ${url}`);

    if (player.getCurrentSource() == url) {
      log.info(`player already has ${url} loaded`);
      return Promise.resolve();
    }

    return player.setSource(url, mimeType).then(
      () => {
        log.debug(`Loaded ${url}`);
        return Promise.resolve();
      },
      err => {
        log.error(`Error when loading source ${url}. ERR:`, err);
        if (!hasRetried) {
          return new Promise((res, rej) => {
            // If we've seen an error that fails to load, wait 50 ms and
            // retry setting the source.
            setTimeout(() => {
              this.setSource(url, mimeType, true).then(res, rej);
            }, 50);
          });
        } else {
          return Promise.reject(err);
        }
      }
    );
  }

  loader(items: ContentItem[]): ContentItem[] {
    const [firstSrc] = items;
    this.setSource(firstSrc.url, firstSrc.data.mime, false);
    return [firstSrc];
  }

  clearSource(): void {
    // This is a null op for the generic player
    log.trace('clearSource called');
  }

  mediaElement(): HTMLMediaElement | undefined {
    if (typeof this.player === 'undefined') {
      log.info('Error player is not defined when trying to get mediaElement');
    }
    if (typeof this.player?.getMediaElement === 'function') {
      return this.player.getMediaElement();
    }
    if (typeof this.player?.getVideoElement === 'function') {
      return this.player.getVideoElement();
    }
    return undefined;
  }

  getAllowedSourceTypes(): SourceType[] {
    return [SourceType.HLS, SourceType.DASH];
  }
}
